import React, { useEffect, useState } from "react";
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardTitle,
	Form,
	Row,
} from "reactstrap";
import shallow from "zustand/shallow";
import {
	DealStatus,
	leaseRollOptions,
	LoanPeriod,
	rtoRollOptions,
} from "constants/Constants";
import formNavigationUtils from "utils/formNavigationUtils";
import useCurrentDealStore from "stores/DealStore";
import useCurrentSaleStore from "stores/SaleStore";
import useCurrentLeaseStore from "stores/LeaseStore";
import { failAlert } from "utils/alertUtils";
import {
	CurrencyInput,
	SimpleSelectorElement,
} from "features/forms/components/InputElement";
import {
	rollByLeasePrice,
	rollByLeaseDown,
	rollByLeaseApr,
	rollByLeaseTerm,
	rollByLeaseResidual,
	rollByMarkup,
	rollByRTOPrice,
	rollByRTODown,
	rollByRTOTerm,
	rollByRTOResidual,
	moneyFactorFromApr,
	calculateDepreciation,
	calculateBasePayment,
	calculateMonthlyTax,
} from "features/deal/helperFunctions/LeaseFunctions";
import swal from "sweetalert2";

const LeaseRollModalBody = ({ toggle }) => {
	// Sale store for roll tracking and down payment
	const {
		payment,
		editRollPreValue,
		editRollBack,
		desiredPmt,
		editDesiredPmt,
		down,
		editDown,
		editPrice,
		editApr,
		editTerm,
	} = useCurrentSaleStore((state) => state, shallow);

	// Lease store - source of truth for lease values
	const {
		price,
		msrp,
		apr,
		term,
		leasePeriod,
		leaseDate,
		firstPmtDate,
		residualValue,
		capReduction,
		capSalesTax,
		netCap,
		capCost,
		grossCapCost,
		leaseType,
		editField: editLeaseField,
	} = useCurrentLeaseStore((state) => state, shallow);

	// Get tax rate from sale store
	const { taxRate } = useCurrentSaleStore(
		(state) => ({ taxRate: state.taxRate }),
		shallow
	);

	// Deal store
	const { type, totalCost } = useCurrentDealStore(
		(state) => ({
			type: state.deal.type,
			totalCost: state.vehicle?.totalCost || 0,
		}),
		shallow
	);

	// Dynamic roll options based on lease type
	const rollOptions = leaseType === 6 ? rtoRollOptions : leaseRollOptions; // Lease uses default options

	const [rollByOpt, setRollByOpt] = useState(rollOptions[0].value);

	/**
	 * Calculate actual payment with new values
	 * IMPORTANT: Must match the rounding in useCalculateLease exactly
	 */
	const calculateActualPayment = (
		netCap,
		residualValue,
		term,
		moneyFactor,
		capSalesTax
	) => {
		const roundUp = (num, digits = 2) => {
			const factor = Math.pow(10, digits);
			return Math.round(num * factor) / factor;
		};

		const depreciation = netCap - residualValue;
		const monthlyDepreciation = roundUp(depreciation / term, 2);
		const monthlyInterest = roundUp((netCap + residualValue) * moneyFactor, 2);
		const basePayment = calculateBasePayment(
			monthlyDepreciation,
			monthlyInterest
		);
		const monthlyTax = calculateMonthlyTax(capSalesTax, basePayment, taxRate);
		return roundUp(basePayment + monthlyTax, 2);
	};

	/**
	 * Show confirmation dialog with payment difference
	 * @param {number} actualPayment - The calculated payment
	 * @returns {Promise<boolean>} - True if user accepts
	 */
	const confirmPaymentDifference = async (actualPayment) => {
		const difference = Math.abs(actualPayment - desiredPmt);

		// Only auto-accept if exact match
		if (actualPayment === desiredPmt) {
			return true;
		}

		const result = await swal.fire({
			title: "Payment Difference",
			html: `
				<p>Due to rounding, the actual payment will be:</p>
				<p style="font-size: 1.2em; font-weight: bold; color: #333;">
					$${actualPayment.toFixed(2)}
				</p>
				<p>Your desired payment: <strong>$${desiredPmt.toFixed(2)}</strong></p>
				<p style="color: #666;">Difference: $${difference.toFixed(2)}</p>
				<p style="margin-top: 10px;">Do you want to proceed?</p>
			`,
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: "Accept",
			cancelButtonText: "Cancel",
		});

		return result.isConfirmed;
	};

	const handleClick = async () => {
		console.log(rollByOpt);
		const rollOption = Number.parseInt(rollByOpt);
		const periodsPerYear = LoanPeriod[leasePeriod].term || 12;

		switch (rollOption) {
			// roll by price
			case 10: {
				// Use RTO function for RTO, Lease function for Lease
				const newPrice =
					leaseType === 6
						? rollByRTOPrice(
								desiredPmt,
								netCap,
								capCost || grossCapCost,
								price,
								term,
								residualValue,
								capReduction,
								capSalesTax,
								taxRate
						  )
						: rollByLeasePrice(
								desiredPmt,
								netCap,
								capCost || grossCapCost,
								price,
								term,
								leasePeriod,
								apr,
								residualValue,
								capReduction,
								capSalesTax,
								taxRate
						  );

				if (newPrice <= 0 || newPrice > 999999) {
					failAlert(
						"Cannot achieve desired payment by adjusting price. Try a different desired payment or roll by a different field."
					);
					return;
				}

				// Calculate new netCap with new price
				const extras = (capCost || grossCapCost) - price;
				const newGrossCapCost = newPrice + extras;
				const newNetCap = newGrossCapCost - capReduction;

				// For RTO: moneyFactor = 0, For Lease: calculate from APR
				const currentMoneyFactor =
					leaseType === 6 ? 0 : moneyFactorFromApr(apr, periodsPerYear);

				// Calculate actual payment and confirm
				const actualPayment = calculateActualPayment(
					newNetCap,
					residualValue,
					term,
					currentMoneyFactor,
					capSalesTax
				);

				const confirmed = await confirmPaymentDifference(actualPayment);
				if (!confirmed) return;

				editRollPreValue(price);
				editRollBack(rollOption);
				editPrice(newPrice);
				editLeaseField("price", newPrice);

				// For RTO: also update markup when price changes
				if (leaseType === 6 && totalCost > 0) {
					const newMarkup = newPrice - totalCost;
					editLeaseField("markup", newMarkup);
				}
				break;
			}
			// roll by down/reduction
			case 20: {
				// Use RTO function for RTO, Lease function for Lease
				const newCapReduction =
					leaseType === 6
						? rollByRTODown(
								desiredPmt,
								netCap,
								capCost || grossCapCost,
								capReduction,
								term,
								residualValue,
								capSalesTax,
								taxRate
						  )
						: rollByLeaseDown(
								desiredPmt,
								netCap,
								capCost || grossCapCost,
								capReduction,
								term,
								leasePeriod,
								apr,
								residualValue,
								capSalesTax,
								taxRate
						  );

				if (
					newCapReduction <= 0 ||
					newCapReduction > (capCost || grossCapCost)
				) {
					failAlert(
						"Cannot achieve desired payment by adjusting down payment. Try a different desired payment or roll by a different field."
					);
					return;
				}

				// Calculate new netCap with new capReduction
				const newNetCapDown = (capCost || grossCapCost) - newCapReduction;
				// For RTO: moneyFactor = 0, For Lease: calculate from APR
				const currentMoneyFactorDown =
					leaseType === 6 ? 0 : moneyFactorFromApr(apr, periodsPerYear);
				const newDown = down + (newCapReduction - capReduction);

				// Calculate actual payment and confirm
				const actualPaymentDown = calculateActualPayment(
					newNetCapDown,
					residualValue,
					term,
					currentMoneyFactorDown,
					capSalesTax
				);

				const confirmedDown = await confirmPaymentDifference(actualPaymentDown);
				if (!confirmedDown) return;

				editRollPreValue(capReduction);
				editRollBack(rollOption);
				// Update both down (for tracking) and capReduction (actual lease calc)
				editDown(newDown);
				editLeaseField("capReduction", newCapReduction);
				break;
			}
			// roll by interest (APR)
			case 30: {
				const newApr = rollByLeaseApr(
					desiredPmt,
					netCap,
					term,
					leasePeriod,
					residualValue,
					capSalesTax,
					taxRate
				);
				console.log(newApr);

				if (newApr <= 0 || newApr > 0.5) {
					failAlert(
						"Cannot achieve desired payment by adjusting APR. The required APR is out of realistic range. Try a different desired payment or roll by a different field."
					);
					return;
				}

				// Update both lease APR and money factor
				const newMoneyFactor = moneyFactorFromApr(newApr, periodsPerYear);

				// Calculate actual payment and confirm
				const actualPaymentApr = calculateActualPayment(
					netCap,
					residualValue,
					term,
					newMoneyFactor,
					capSalesTax
				);

				const confirmedApr = await confirmPaymentDifference(actualPaymentApr);
				if (!confirmedApr) return;

				editRollPreValue(apr);
				editRollBack(rollOption);
				editApr(newApr);
				editLeaseField("apr", newApr);
				editLeaseField("moneyFactor", Number(newMoneyFactor.toFixed(8)));
				break;
			}
			// roll by markup (RTO only)
			case 35: {
				const newMarkup = rollByMarkup(
					desiredPmt,
					netCap,
					capCost || grossCapCost,
					lease.markup || 0,
					totalCost,
					term,
					leasePeriod,
					apr,
					residualValue,
					capReduction,
					capSalesTax,
					taxRate
				);

				if (!isFinite(newMarkup)) {
					failAlert(
						"Cannot achieve desired payment by adjusting markup. Try a different desired payment or roll by a different field."
					);
					return;
				}

				// Calculate new price from markup
				const newPrice = totalCost + newMarkup;

				// Calculate new netCap with new price
				const extras = (capCost || grossCapCost) - price;
				const newGrossCapCost = newPrice + extras;
				const newNetCap = newGrossCapCost - capReduction;
				const currentMoneyFactorMarkup = moneyFactorFromApr(
					apr,
					periodsPerYear
				);

				// Calculate actual payment and confirm
				const actualPaymentMarkup = calculateActualPayment(
					newNetCap,
					residualValue,
					term,
					currentMoneyFactorMarkup,
					capSalesTax
				);

				const confirmedMarkup = await confirmPaymentDifference(
					actualPaymentMarkup
				);
				if (!confirmedMarkup) return;

				editRollPreValue(lease.markup || 0);
				editRollBack(rollOption);
				editLeaseField("markup", newMarkup);
				editLeaseField("price", newPrice);
				editPrice(newPrice);
				break;
			}
			// roll by term
			case 40: {
				// Use RTO function for RTO, Lease function for Lease
				const newTerm =
					leaseType === 6
						? rollByRTOTerm(
								desiredPmt,
								netCap,
								residualValue,
								capSalesTax,
								taxRate
						  )
						: rollByLeaseTerm(
								desiredPmt,
								netCap,
								leasePeriod,
								apr,
								residualValue,
								capSalesTax,
								taxRate
						  );

				if (newTerm <= 1 || newTerm > 120) {
					failAlert(
						"Cannot achieve desired payment by adjusting term. The required term is out of realistic range. Try a different desired payment or roll by a different field."
					);
					return;
				}

				// Calculate actual payment and confirm
				// For RTO: moneyFactor = 0, For Lease: calculate from APR
				const currentMoneyFactorTerm =
					leaseType === 6 ? 0 : moneyFactorFromApr(apr, periodsPerYear);
				const actualPaymentTerm = calculateActualPayment(
					netCap,
					residualValue,
					newTerm,
					currentMoneyFactorTerm,
					capSalesTax
				);

				const confirmedTerm = await confirmPaymentDifference(actualPaymentTerm);
				if (!confirmedTerm) return;

				editRollPreValue(term);
				editRollBack(rollOption);
				editTerm(newTerm);
				editLeaseField("term", newTerm);
				break;
			}
			// roll by residual
			case 60: {
				// Use RTO function for RTO, Lease function for Lease
				const newResidual =
					leaseType === 6
						? rollByRTOResidual(
								desiredPmt,
								netCap,
								term,
								capSalesTax,
								taxRate
						  )
						: rollByLeaseResidual(
								desiredPmt,
								netCap,
								term,
								leasePeriod,
								apr,
								capSalesTax,
								taxRate,
								msrp // For Lease, use msrp
						  );

				if (newResidual <= 0 || newResidual > price) {
					failAlert(
						"Cannot achieve desired payment by adjusting residual value. Try a different desired payment or roll by a different field."
					);
					return;
				}

				// Calculate actual payment and confirm
				// For RTO: moneyFactor = 0, For Lease: calculate from APR
				const currentMoneyFactorResidual =
					leaseType === 6 ? 0 : moneyFactorFromApr(apr, periodsPerYear);
				const actualPaymentResidual = calculateActualPayment(
					netCap,
					newResidual,
					term,
					currentMoneyFactorResidual,
					capSalesTax
				);

				const confirmedResidual = await confirmPaymentDifference(
					actualPaymentResidual
				);
				if (!confirmedResidual) return;

				editRollPreValue(residualValue);
				editRollBack(rollOption);
				editLeaseField("residualValue", newResidual);
				break;
			}
			default:
				break;
		}
		toggle();
	};

	useEffect(() => {
		editDesiredPmt(payment);
		// eslint-disable-next-line
	}, []);

	return (
		<Form onKeyDown={formNavigationUtils}>
			<Card>
				<CardHeader>
					<CardTitle tag="h3">Roll By Payment</CardTitle>
				</CardHeader>
				<CardBody>
					<Row>
						<SimpleSelectorElement
							readOnly={type !== DealStatus.PENDING}
							label="Roll By Option"
							value={rollByOpt}
							colSize="col-md-6"
							onChange={(e) => {
								setRollByOpt(e.target.value);
							}}
							options={rollOptions}
						/>
						<CurrencyInput
							label="Desired Payment"
							readOnly={type !== DealStatus.PENDING}
							value={desiredPmt}
							onChange={editDesiredPmt}
							name="desiredPmt"
							disableValidation
							wholeRow
						/>
					</Row>
					<div className="text-center">
						<Button
							readOnly={type !== DealStatus.PENDING}
							onClick={handleClick}
						>
							Calculate
						</Button>
					</div>
				</CardBody>
			</Card>
		</Form>
	);
};

export default LeaseRollModalBody;
